// Persist the last discarded transaction's editable form
// state (localStorage, single TTL'd entry) for reopen.

import { DISCARDED_FORM_TTL_MS } from './constants';

// Single fixed key: only the last discarded transaction is ever stored.
const STORAGE_KEY = 'aiq:discardedForm:last';
// Legacy per-transaction prefix from the previous multi-entry implementation.
// Swept on boot so stale entries don't linger after deploy.
const LEGACY_STORAGE_PREFIX = 'aiq:discardedForm:';

const sameTransaction = (a, b) => a != null && b != null && String(a) === String(b);

// Lightweight telemetry hook. When operator-entered data cannot be persisted
// (e.g. QuotaExceededError) we want more than a console.warn so the team can
// detect it in the field. Falls back to console.warn when no reporter is wired.
let reportStorageIssue = (context, err) => {
    console.warn(`discardedFormCache: ${context}`, err);
};

/**
 * Allow the app to inject a real telemetry/error reporter (e.g. Sentry) so
 * storage failures on operator-entered data are observable in production.
 * @param {(context: string, err: unknown) => void} reporter
 */
export function setDiscardedFormReporter(reporter) {
    if (typeof reporter === 'function') reportStorageIssue = reporter;
}

/**
 * Persist a snapshot of the editable form state for the last discarded
 * transaction. Overwrites any previously stored snapshot.
 *
 * @param {string|number} transactionId
 * @param {{ values?: object, userEdited?: object, fieldValueSources?: object }} snapshot
 */
export function saveDiscardedForm(transactionId, snapshot) {
    if (transactionId == null || !snapshot) return;
    try {
        const payload = {
            transactionId: String(transactionId),
            values: snapshot.values || {},
            userEdited: snapshot.userEdited || {},
            fieldValueSources: snapshot.fieldValueSources || {},
            timestamp: Date.now(),
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (err) {
        // Most likely QuotaExceededError. Operator data won't be recoverable on
        // reopen; surface via telemetry rather than swallowing silently.
        reportStorageIssue(`saveDiscardedForm(): failed to persist discarded form for transactionId=${transactionId}`, err);
    }
}

/**
 * Read back the stored discarded form snapshot for a given transaction. Returns
 * null when there is no snapshot, when it belongs to a different transaction, or
 * when it has expired (expired/mismatched entries are removed as a side effect).
 *
 * @param {string|number} transactionId
 * @returns {{ transactionId: string, values: object, userEdited: object, fieldValueSources: object, timestamp: number } | null}
 */
export function getDiscardedForm(transactionId) {
    if (transactionId == null) return null;
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') {
            clearDiscardedForm();
            return null;
        }
        if (!parsed.timestamp || Date.now() - parsed.timestamp > DISCARDED_FORM_TTL_MS) {
            clearDiscardedForm();
            return null;
        }
        // Only the last discarded transaction is retained; if the caller asks for
        // a different one there is nothing to restore for it.
        if (!sameTransaction(parsed.transactionId, transactionId)) return null;
        return parsed;
    } catch (err) {
        reportStorageIssue(`getDiscardedForm(): failed to read discarded form for transactionId=${transactionId}`, err);
        clearDiscardedForm();
        return null;
    }
}

/**
 * Remove the stored discarded form snapshot. When a transactionId is supplied,
 * the snapshot is only removed if it belongs to that transaction (so one tab
 * can't wipe a snapshot that now belongs to a different transaction). Call with
 * no argument to unconditionally clear.
 *
 * @param {string|number} [transactionId]
 */
export function clearDiscardedForm(transactionId) {
    try {
        if (transactionId == null) {
            localStorage.removeItem(STORAGE_KEY);
            return;
        }
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return;
        let parsed;
        try {
            parsed = JSON.parse(raw);
        } catch {
            // Corrupt entry — safe to drop regardless of id.
            localStorage.removeItem(STORAGE_KEY);
            return;
        }
        if (!parsed || sameTransaction(parsed.transactionId, transactionId)) {
            localStorage.removeItem(STORAGE_KEY);
        }
    } catch (err) {
        reportStorageIssue(`clearDiscardedForm(): failed to clear discarded form for transactionId=${transactionId}`, err);
    }
}

/**
 * Remove the stored discarded-form snapshot if it is expired/corrupt, and sweep
 * any legacy per-transaction entries left by the previous implementation. Safe
 * to call opportunistically (e.g. on app boot).
 */
export function pruneExpiredDiscardedForms() {
    try {
        const now = Date.now();

        // 1) The current single entry — drop if missing timestamp / expired / corrupt.
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw != null) {
            try {
                const parsed = JSON.parse(raw);
                if (!parsed?.timestamp || now - parsed.timestamp > DISCARDED_FORM_TTL_MS) {
                    localStorage.removeItem(STORAGE_KEY);
                }
            } catch {
                localStorage.removeItem(STORAGE_KEY);
            }
        }

        // 2) Legacy per-transaction entries (aiq:discardedForm:<id>) from the old
        // multi-entry version. Remove them all — only the single key is used now.
        for (let i = localStorage.length - 1; i >= 0; i--) {
            const key = localStorage.key(i);
            if (!key || key === STORAGE_KEY || !key.startsWith(LEGACY_STORAGE_PREFIX)) continue;
            localStorage.removeItem(key);
        }
    } catch (err) {
        reportStorageIssue('pruneExpiredDiscardedForms(): failed to prune discarded forms', err);
    }
}
